import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { UiTranslationService } from '@core/services/ui-translation.service';
import {
  TrainCardSubType,
  TrainFlashcard,
  TrainOption,
} from '../../../deck-train.models';

@Component({
  selector: 'app-text-multiple-choice-train-back',
  imports: [],
  templateUrl: './text-multiple-choice-train-back.html',
  styleUrl: './text-multiple-choice-train-back.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.text-multiple-choice-train-back--flow]': 'hasFrontMedia()',
  },
})
export class TextMultipleChoiceTrainBack {
  private readonly deckTranslations =
    inject(UiTranslationService).namespace('deck');
  private readonly optionShuffleSeed = Math.random();

  readonly card = input.required<TrainFlashcard>();
  readonly isBackVisible = input.required<boolean>();
  readonly hasFrontMedia = input(false);
  readonly isCorrect = input<boolean | null>(null);
  readonly selectedOptionId = input<number | null>(null);
  readonly selectedOptionIds = input<number[]>([]);
  readonly playableOptionIds = input<number[]>([]);

  readonly selectedOptionIdChange = output<number>();
  readonly selectedOptionIdsChange = output<number[]>();
  readonly optionSpeak = output<number>();

  readonly t = this.deckTranslations.t;
  protected readonly shuffledOptions = computed(() =>
    this.shuffleOptions(this.card()),
  );

  get missedAnswer(): boolean {
    return (
      this.isBackVisible() &&
      this.isCorrect() === false &&
      this.selectedIds().length === 0
    );
  }

  get isMultipleSelection(): boolean {
    return this.card().cardSubType === TrainCardSubType.TextMultipleChoice;
  }

  selectOption(option: TrainOption): void {
    if (this.isBackVisible()) {
      return;
    }

    if (this.isMultipleSelection) {
      this.selectedOptionIdsChange.emit(this.toggleSelectedOption(option.id));
    } else {
      this.selectedOptionIdChange.emit(option.id);
    }

    if (this.canPlayOption(option)) {
      this.optionSpeak.emit(option.id);
    }
  }

  optionAriaLabel(option: TrainOption): string {
    const label = this.optionBaseAriaLabel(option);
    if (!this.isBackVisible() && this.canPlayOption(option)) {
      return `${label}. ${this.t()['train.playAudio']}`;
    }

    return label;
  }

  protected isOptionSelected(option: TrainOption): boolean {
    return this.selectedIds().includes(option.id);
  }

  private canPlayOption(option: TrainOption): boolean {
    return this.playableOptionIds().includes(option.id);
  }

  private optionBaseAriaLabel(option: TrainOption): string {
    if (!this.isBackVisible()) {
      if (this.isOptionSelected(option)) {
        return `${this.t()['train.selectedAnswer']}: ${option.text}`;
      }

      return `${this.t()['train.selectAnswer']}: ${option.text}`;
    }

    if (option.isCorrect) {
      return `${this.t()['train.correctOption']}: ${option.text}`;
    }

    if (this.isOptionSelected(option) && this.isCorrect() === false) {
      return `${this.t()['train.incorrectOption']}: ${option.text}`;
    }

    return option.text;
  }

  private selectedIds(): number[] {
    if (this.isMultipleSelection) {
      return this.selectedOptionIds();
    }

    const selectedOptionId = this.selectedOptionId();
    return selectedOptionId == null ? [] : [selectedOptionId];
  }

  private toggleSelectedOption(optionId: number): number[] {
    const selectedIds = this.selectedOptionIds();
    if (selectedIds.includes(optionId)) {
      return selectedIds.filter((selectedId) => selectedId !== optionId);
    }

    return [...selectedIds, optionId];
  }

  private shuffleOptions(card: TrainFlashcard): TrainOption[] {
    const options = [...card.options];
    if (options.length < 2) {
      return options;
    }

    let seed = this.createShuffleSeed(card);
    for (let index = options.length - 1; index > 0; index -= 1) {
      seed = this.nextShuffleSeed(seed);
      const swapIndex = seed % (index + 1);
      [options[index], options[swapIndex]] = [
        options[swapIndex],
        options[index],
      ];
    }

    if (this.isSameOptionOrder(card.options, options)) {
      options.push(options.shift() as TrainOption);
    }

    return options;
  }

  private createShuffleSeed(card: TrainFlashcard): number {
    let seed =
      (Math.floor(this.optionShuffleSeed * 0xffffffff) ^ card.id) >>> 0;

    for (const option of card.options) {
      seed = (seed ^ option.id) >>> 0;
      seed = this.nextShuffleSeed(seed);
    }

    return seed || 1;
  }

  private nextShuffleSeed(seed: number): number {
    return (Math.imul(seed ^ (seed >>> 15), 0x85ebca6b) + 0x9e3779b9) >>> 0;
  }

  private isSameOptionOrder(
    originalOptions: TrainOption[],
    shuffledOptions: TrainOption[],
  ): boolean {
    return originalOptions.every(
      (option, index) => option.id === shuffledOptions[index]?.id,
    );
  }
}
